import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  MessageSquare, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  User,
  Calendar
} from 'lucide-react';

interface ActivityItem {
  id: string;
  type: 'message' | 'document' | 'status_change' | 'assignment' | 'deadline';
  title: string;
  description: string;
  timestamp: string;
  user: {
    name: string;
    role: 'client' | 'internal' | 'reviewer';
  };
  projectTitle: string;
  priority?: 'low' | 'medium' | 'high';
}

interface ActivityFeedProps {
  activities: ActivityItem[];
}

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'message': return <MessageSquare className="h-4 w-4" />;
    case 'document': return <FileText className="h-4 w-4" />;
    case 'status_change': return <CheckCircle className="h-4 w-4" />;
    case 'assignment': return <User className="h-4 w-4" />;
    case 'deadline': return <Clock className="h-4 w-4" />;
    default: return <AlertCircle className="h-4 w-4" />;
  }
};

const getRoleColor = (role: string) => {
  switch (role) {
    case 'client': return 'bg-blue-100 text-blue-800';
    case 'internal': return 'bg-green-100 text-green-800';
    case 'reviewer': return 'bg-purple-100 text-purple-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getActivityColor = (type: string) => {
  switch (type) {
    case 'message': return 'text-blue-600 bg-blue-50';
    case 'document': return 'text-green-600 bg-green-50';
    case 'status_change': return 'text-purple-600 bg-purple-50';
    case 'assignment': return 'text-orange-600 bg-orange-50';
    case 'deadline': return 'text-red-600 bg-red-50';
    default: return 'text-gray-600 bg-gray-50';
  }
};

export const ActivityFeed = ({ activities }: ActivityFeedProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {activities.map((activity) => (
            <div key={activity.id} className="flex gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors">
              <div className={`p-2 rounded-full ${getActivityColor(activity.type)}`}>
                {getActivityIcon(activity.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {activity.title}
                  </h4>
                  <time className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                    {activity.timestamp}
                  </time>
                </div>
                
                <p className="text-sm text-muted-foreground mb-2">
                  {activity.description}
                </p>
                
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="text-xs">
                    {activity.projectTitle}
                  </Badge>
                  <Badge className={getRoleColor(activity.user.role)}>
                    {activity.user.name} ({activity.user.role})
                  </Badge>
                  {activity.priority && (
                    <Badge variant="outline" className={`text-xs ${
                      activity.priority === 'high' ? 'border-red-300 text-red-700' :
                      activity.priority === 'medium' ? 'border-yellow-300 text-yellow-700' :
                      'border-green-300 text-green-700'
                    }`}>
                      {activity.priority} priority
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};